// mcpHealthApi — cross-domain fetcher for /mcp/health.
//
// Called from /oauth/grant on `nexus.sentry.red` → reaches
// `mcp.nexus.sentry.red/mcp/health`. The worker's CORS
// middleware echoes the request Origin back when present, so a
// no-credentials GET succeeds without preflight.
//
// Used by the OAuth consent page to render truthful per-category
// counts (read / write / execute) without hardcoding numbers that
// drift when we extend the whitelist.

const MCP_HEALTH_URL = "https://mcp.nexus.sentry.red/mcp/health";

export interface McpToolsByCategory {
  read: number;
  write: number;
  execute: number;
  total: number;
}

export interface McpHealth {
  ok: boolean;
  service: string;
  build_sha: string | null;
  tools_exposed: number;
  tools_by_category?: McpToolsByCategory;
  schema_drift: string[];
  oauth_configured: boolean;
  correlation_id: string | null;
}

/** Hardcoded fallback used when /mcp/health is unreachable or doesn't
 *  yet include the breakdown (e.g. older deploy). Numbers match the
 *  whitelist at time of writing — they go stale if mcp-api is upgraded
 *  before frontend; that's why the live fetch is preferred. */
export const MCP_TOOLS_FALLBACK: McpToolsByCategory = {
  read: 26,
  write: 26,
  execute: 2,
  total: 54,
};

/** Returns parsed health JSON. Throws on network / non-200 — caller
 *  should `.catch(() => MCP_TOOLS_FALLBACK)` to keep the UI rendering
 *  even when the worker is down. 5-second timeout via AbortController
 *  so the consent page never blocks more than that on a stuck mcp-api. */
export async function fetchMcpHealth(signal?: AbortSignal): Promise<McpHealth> {
  // Compose external + caller signals so the caller can also abort.
  const local = new AbortController();
  const timer = setTimeout(() => local.abort(), 5000);
  const onExternalAbort = (): void => local.abort();
  if (signal) signal.addEventListener("abort", onExternalAbort, { once: true });

  try {
    const r = await fetch(MCP_HEALTH_URL, {
      method: "GET",
      mode: "cors",
      cache: "no-store",
      signal: local.signal,
    });
    if (!r.ok) throw new Error(`mcp-health http_${r.status}`);
    return (await r.json()) as McpHealth;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onExternalAbort);
  }
}

/** Convenience wrapper used by /oauth/grant — never throws; returns
 *  the live breakdown when reachable, fallback constants otherwise. */
export async function getMcpToolsBreakdown(signal?: AbortSignal): Promise<McpToolsByCategory> {
  try {
    const h = await fetchMcpHealth(signal);
    return h.tools_by_category ?? {
      // /mcp/health alive but on an old deploy without the breakdown
      // field — derive total + zero out unknown splits as a tertiary
      // fallback so the UI still shows a number.
      read: MCP_TOOLS_FALLBACK.read,
      write: MCP_TOOLS_FALLBACK.write,
      execute: MCP_TOOLS_FALLBACK.execute,
      total: h.tools_exposed ?? MCP_TOOLS_FALLBACK.total,
    };
  } catch {
    return MCP_TOOLS_FALLBACK;
  }
}
